import { ReactNode, useEffect, useState } from 'react'
import { Box, IconButton, Tooltip } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { Group, Panel, Separator, useDefaultLayout, usePanelRef } from 'react-resizable-panels'

export type ResizableSplitProps = {
  // Persists the split ratio (and thereby the collapsed state) in localStorage.
  autoSaveId: string
  left: ReactNode
  // Render prop so the right panel can place its own collapse affordance (e.g. in a header row).
  right: (args: { collapse: () => void }) => ReactNode
  expandLabel?: string
  leftMinSize?: string
  rightMinSize?: string
}

/**
 * Horizontal two-pane layout with a draggable divider. The right pane snaps closed when dragged below its minimum
 * size (or via the render-prop `collapse` callback) and reopens from the chevron that replaces it.
 */
const ResizableSplit = ({
  autoSaveId,
  left,
  right,
  expandLabel = 'Show panel',
  leftMinSize = '25%',
  rightMinSize = '20%',
}: ResizableSplitProps) => {
  const rightPanelRef = usePanelRef()
  const [collapsed, setCollapsed] = useState(false)
  const [dragging, setDragging] = useState(false)
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({ id: autoSaveId })

  // The library only reports drag state internally, so track it from the separator's pointer events.
  useEffect(() => {
    if (!dragging) return
    const stop = () => setDragging(false)
    window.addEventListener('pointerup', stop)
    window.addEventListener('pointercancel', stop)
    return () => {
      window.removeEventListener('pointerup', stop)
      window.removeEventListener('pointercancel', stop)
    }
  }, [dragging])

  return (
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
      <Group
        orientation="horizontal"
        defaultLayout={defaultLayout}
        onLayoutChanged={onLayoutChanged}
        style={{ flex: 1, minHeight: 0 }}
      >
        <Panel id="left" defaultSize="50%" minSize={leftMinSize}>
          <Box sx={{ height: '100%', display: 'flex', pr: 2 }}>{left}</Box>
        </Panel>
        <Separator onPointerDown={() => setDragging(true)} aria-label="resize panels">
          <Box
            sx={{
              width: '6px',
              height: '100%',
              borderRadius: 1,
              cursor: 'col-resize',
              bgcolor: dragging ? 'primary.main' : 'divider',
              '&:hover': { bgcolor: 'primary.main' },
              transition: 'background-color 120ms',
            }}
          />
        </Separator>
        <Panel
          id="right"
          defaultSize="50%"
          minSize={rightMinSize}
          collapsible
          collapsedSize={0}
          panelRef={rightPanelRef}
          // Also fires on mount, syncing `collapsed` with a persisted collapsed layout.
          onResize={(size) => setCollapsed(size.inPixels < 1)}
        >
          <Box sx={{ height: '100%', display: 'flex', pl: 2, overflow: 'hidden' }}>
            {right({ collapse: () => rightPanelRef.current?.collapse() })}
          </Box>
        </Panel>
      </Group>
      {collapsed && (
        <Tooltip title={expandLabel}>
          <IconButton
            aria-label={expandLabel}
            size="small"
            onClick={() => rightPanelRef.current?.expand()}
            sx={{ alignSelf: 'flex-start', ml: 0.5 }}
          >
            <ChevronLeftIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )
}

export default ResizableSplit
